import { call, put, select } from 'redux-saga/effects';
import { types as loginTypes } from '@reducers/login';
import * as Api from '@utils/Api';
import history from '../history';
import { popDest } from '@utils/intendedDest';
import { twoFactorChallengeTokenSelector } from '../selectors/Login';
import { navigate } from '@reducers/Navigation';
import { querySelector } from '../selectors/Router';
import { logEvent, USER_LOGOUT_CLICK, shutdown } from 'utils/analytics';

const redirectToIntended = () => {
  const intended = popDest() || '/dash';
  history.push(intended);
};

export function* login(action) {
  const { email, password } = action;
  if (!email || !password) {
    throw new Error('Missing required parameters for calling login API.');
  }
  const isAuthRequest = true;
  const response = yield call(
    Api.post,
    'login',
    { email, password },
    isAuthRequest
  );
  if (!response.success) {
    yield put({
      type: loginTypes.LOGIN_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    const twoFactorChallengeToken = response.data['2fa_challenge_token'];
    yield put({
      type: loginTypes.LOGIN_SUCCESS,
      twoFactorChallengeToken,
    });
    if (!twoFactorChallengeToken) {
      redirectToIntended();
    }
  }
}

export function* logout() {
  yield call(shutdown);
  yield call(logEvent, USER_LOGOUT_CLICK);
  yield call(Api.post, '/logout');
  yield call([window.location, window.location.replace], '/login');
}

export function* verify(action) {
  const { challengeToken, twoFactorCode } = action;
  if (!challengeToken || !twoFactorCode) {
    throw new Error(
      'Missing required parameters for calling login two-factor verification API.'
    );
  }
  const isAuthRequest = true;
  const response = yield call(
    Api.post,
    'login/two-factor-verify',
    {
      challenge_token: challengeToken,
      two_factor_code: twoFactorCode,
    },
    isAuthRequest
  );
  if (!response.success) {
    yield put({
      type: loginTypes.VERIFY_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      clearToken: response.status === 401,
    });
  } else {
    yield put({
      type: loginTypes.VERIFY_SUCCESS,
    });
    redirectToIntended();
  }
}

export function* stopImpersonating() {
  yield call(Api.get, '/logout', false);
}

export function* requestResetEmail(action) {
  const { email } = action;
  if (!email) {
    throw new Error(
      'Missing required parameters for calling request-reset-email API'
    );
  }

  const response = yield call(Api.post, 'request-password-reset', { email });

  if (!response.success) {
    yield put({
      type: loginTypes.REQUEST_RESET_EMAIL_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: loginTypes.REQUEST_RESET_EMAIL_SUCCESS,
    });
  }
}

export function* resetPassword(action) {
  const { email, password, password_confirmation, token } = action;
  if (!email || !password || !password_confirmation || !token) {
    throw new Error(
      'Missing required parameters for calling reset-password API'
    );
  }

  const response = yield call(Api.post, 'password-reset', {
    email,
    password,
    password_confirmation,
    token,
  });

  if (!response.success) {
    yield put({
      type: loginTypes.RESET_PASSWORD_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      message: response.data ? response.data.message : null,
    });
  } else {
    yield put({
      type: loginTypes.RESET_PASSWORD_SUCCESS,
    });
    yield put({
      type: loginTypes.LOGIN_REQUEST,
      email,
      password,
    });
  }
}

export function* tryLoginVerifyMissingUserRedirect(pathname) {
  const twoFactorChallengeToken = yield select(twoFactorChallengeTokenSelector);
  const query = yield select(querySelector);
  const twoFactorChallengeTokenFromOauth = query?.['2fa_challenge_token'];

  // We retrieve the two factor challenge token either from state or from the query string parameter (OAuth)
  if (
    pathname === '/login/verify' &&
    !twoFactorChallengeToken &&
    !twoFactorChallengeTokenFromOauth
  ) {
    yield put(navigate({ href: '/login' }));
    return true;
  }
  return false;
}
